import PropTypes from "prop-types";
import { createRef, Component } from "react";
import ReactDOM from "react-dom";
import { webix } from "extensions/webix";

import { destroyPopovers } from "common/utils";

import { Webix } from "./webix";

export class WebixDatatable extends Component {
    static defaultProps = {
        refreshRowHeightAfterColumnsChanged: false,
        className: ""
    };
    
    static propTypes = {
        refreshRowHeightAfterColumnsChanged: PropTypes.bool,
        className: PropTypes.string,
        data: PropTypes.arrayOf(PropTypes.shape()).isRequired
    };
    
    constructor(props) {
        super(props);
        this.table = createRef();
    }
    
    componentDidMount() {
        window.addEventListener("resize", this.updateTableDimensions);
    }
    
    componentDidUpdate(prevProps) {
        if (this.columnsHaveChanged(prevProps)) {
            this.unmountHeaderTooltips();
            // Update the table with new columns
            this.table.current.ui.refreshColumns(this.props.ui.columns);
            
            if (this.props.refreshRowHeightAfterColumnsChanged) {
                this.table.current.ui.adjustRowHeight();
            }
        }
    }
    
    componentWillUnmount() {
        window.removeEventListener("resize", this.updateTableDimensions);
        this.unmountHeaderTooltips();
    }

    unmountHeaderTooltips = () => {
        if (this.table.current) {
            Array.from(this.table.current.ui.getNode().getElementsByClassName("webix_hcell")).forEach((container) => ReactDOM.unmountComponentAtNode(container));
        }
    };

    updateTableDimensions = () => {
        this.table.current.ui.adjust();
    };
    
    // Check if columns have changed between updates
    columnsHaveChanged(prevProps) {
        const iterateeFn = ({ id }) => id;
        const filterFn = ({ id }) => id !== "blank_filler";
        return JSON.stringify(this.props.ui.columns.filter(filterFn).map(iterateeFn)) !== JSON.stringify(prevProps.ui.columns.filter(filterFn).map(iterateeFn));
    }
    
    toHTML() {
        return webix.toHTML(this.table.current.ui).outerHTML;
    }
    
    toExcel(opts = {}) {
        return webix.toExcel(this.table.current.ui, opts);
    }
    
    toCSV(opts = {}) {
        return webix.toCSV(this.table.current.ui, opts);
    }
    
    render() {
        const events = this.props.ui.on;
        
        const ui = {
            scrollAlignY: false, // https://docs.webix.com/api__ui.datatable_scrollaligny_config.html
            resizeColumn: { size: 10 }, // https://docs.webix.com/datatable__sizing.html#resizing
            ...this.props.ui,
            on: {
                ...this.props.ui.on,
                onAfterScroll() {
                    if (ui.removePopoversOnScroll) {
                        // TODO: we should stop using evil bootstrap popovers for any new features.
                        destroyPopovers();
                    }
                    
                    return events?.onHeaderClick?.apply(this, arguments);
                },
                onAfterLoad() {
                    events?.onAfterLoad?.apply(this, arguments);
                    
                    // Hide blank_filler column if there is no extra space
                    const center_frame = this._body.querySelector(".webix_ss_center");
                    const center_scroll = center_frame.querySelector(".webix_ss_center_scroll");
                    const fillerColumn = {
                        id: "blank_filler",
                        header: null,
                        css: "blank_filler",
                        fillspace: true
                    };
                    
                    if (center_scroll.clientWidth < center_frame.clientWidth) {
                        // Filter column is not visible and not present
                        this.config.columns.push({ ...fillerColumn });
                        this.refreshColumns();
                    } else if (!this.isColumnVisible("blank_filler")) {
                        // Filler column is there but hidden
                        this.showColumn("blank_filler");
                    } else if (center_scroll.clientWidth !== center_frame.clientWidth && this.isColumnVisible(fillerColumn.id) && this.getColumnIndex(fillerColumn.id) !== -1) {
                        this.hideColumn("blank_filler");
                    }
                }
            },
            view: "datatable"
        };
        
        return <Webix className={this.props.className} ui={ui} data={this.props.data} ref={this.table} preserveScrollState/>;
    }
}
